import React, { useEffect, useMemo, useState } from 'react'
import { func } from 'prop-types'

import { FileDnDContext } from './file-dnd-context'

const canUseDnD = (function () {
    const div = document.createElement('div')
    return (
        ('draggable' in div || ('ondragstart' in div && 'ondrop' in div)) &&
        'FormData' in window &&
        'FileReader' in window
    )
})()

const propTypes = {
    validateFileItems: func
}

const defaultProps = {
    validateFileItems: () => true
}

/**
 * Context provider for file drag and drop handling. The components below this provider can subscribe to its context to get
 * data needed to handle dnd for files. This includes f.e. whether a file is currently dragged. For more information
 * what's included in the context have a look in the FileDnDContext.
 *
 *
 * @param props
 * @property {function(DataTransferItemList): boolean} props.validateFileItems - Function to validate the file list while dragging and prevent handling if invalid
 * @return {JSX.Element}
 * @constructor
 */
const FileDndContextProvider = function ({ children, validateFileItems }) {
    const [dataTransfer, setDataTransfer] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const [currentHoveredDropzoneId, setCurrentHoveredDropzoneId] = useState(null)

    useEffect(() => {
        if (!canUseDnD) return

        let draggingFlag = false
        let timoutId = null

        /**
         * @param {DataTransferItemList} items
         * @return {boolean}
         */
        const validateTransferItems = items => {
            if (!items.length) {
                return true // safari does not provide a items list
            }

            for (let i = 0; i < items.length; i++) {
                if (items[i].kind !== 'file') {
                    items.remove(i)
                }
            }
            return validateFileItems(items)
        }

        const isValidDragEvent = event => {
            return (
                event?.dataTransfer &&
                event.dataTransfer.types.includes('Files') &&
                validateTransferItems(event.dataTransfer.items)
            )
        }

        /**
         * @param {DragEvent} event
         */
        const onDragEnter = event => {
            if (!isDragging && isValidDragEvent(event)) {
                setDataTransfer(event.dataTransfer)
                draggingFlag = true
                setIsDragging(true)
            }
        }

        /**
         * @param {DragEvent} event
         */
        const onDragOver = event => {
            if (isValidDragEvent(event)) {
                draggingFlag = true
            }
            if (isDragging) {
                // necessary for the drop event to fire
                // see https://stackoverflow.com/questions/50230048/react-ondrop-is-not-firing#comment92503315_50233827
                event.stopPropagation()
                event.preventDefault()
            }
        }

        /**
         * @param {DragEvent} event
         */
        const onDragEnd = event => {
            clearTimeout(timoutId)
            if (isDragging) {
                // delay update if "isDragging" to let drop event be fired
                setTimeout(function () {
                    setIsDragging(false)
                })
            }

            event.preventDefault()
        }

        const onDragLeave = () => {
            draggingFlag = false
            clearTimeout(timoutId)
            timoutId = setTimeout(function () {
                !draggingFlag && setIsDragging(false)
            }, 200)
        }

        document.documentElement.addEventListener('dragenter', onDragEnter)
        document.documentElement.addEventListener('dragleave', onDragLeave)
        document.documentElement.addEventListener('dragend', onDragEnd)
        document.documentElement.addEventListener('dragover', onDragOver)
        document.documentElement.addEventListener('drop', onDragEnd)

        return () => {
            document.documentElement.removeEventListener('dragenter', onDragEnter)
            document.documentElement.removeEventListener('dragleave', onDragLeave)
            document.documentElement.removeEventListener('dragend', onDragEnd)
            document.documentElement.removeEventListener('dragover', onDragOver)
            document.documentElement.removeEventListener('drop', onDragEnd)
            clearTimeout(timoutId)
        }
    }, [isDragging, validateFileItems])

    const context = useMemo(() => {
        return {
            dataTransfer,
            isDragging,
            currentHoveredDropzoneId,
            setCurrentHoveredDropzoneId
        }
    }, [dataTransfer, isDragging, currentHoveredDropzoneId, setCurrentHoveredDropzoneId])

    if (!canUseDnD) {
        return children
    }

    return <FileDnDContext.Provider value={context}>{children}</FileDnDContext.Provider>
}

FileDndContextProvider.propTypes = propTypes
FileDndContextProvider.defaultProps = defaultProps

export { FileDndContextProvider }
